<template>
    <div>
        <!-- Belge güncelleme: mevcut belge silinmez, yeni sürüm olarak eklenir. -->
        <b-modal id="update-agreement-document" title="Sözleşme Belgesi Yükle" no-close-on-esc no-close-on-backdrop v-on:hidden="resetForm">
            <b-alert v-if="hasDocument" show variant="warning" class="p-1 mb-1">
                Mevcut belge <b>v{{ agreement.AgreementDocumentVersion }}</b> olarak geçmişe taşınacak,
                yüklediğiniz dosya güncel sözleşme belgesi olacaktır. Eski sürümler silinmez.
            </b-alert>
            <div class="mb-1">
                <label class="form-label">Sözleşme Belgesi (PDF) <span class="text-danger">*</span></label>
                <v-file-uploader :accept="['pdf']" v-model="File" valueParameter="AgreementDocument"></v-file-uploader>
            </div>
            <div>
                <label class="form-label">Değişiklik Notu <span class="text-danger">*</span></label>
                <b-form-textarea
                    v-model="RevisionNote"
                    rows="3"
                    maxlength="500"
                    placeholder="Belgenin neden yüklendiğini/güncellendiğini yazınız."></b-form-textarea>
                <small class="text-muted">Geçmiş listesinde bu not görüntülenir.</small>
            </div>
            <template #modal-footer>
                <v-executer
                    text="Kaydet"
                    :disabled="!canUpload"
                    :loading="fileUploading"
                    :action="fileUpload"></v-executer>
            </template>
        </b-modal>

        <b-modal id="agreement-document-history" title="Sözleşme Belgesi Geçmişi" size="lg" hide-footer>
            <div class="position-relative" style="min-height:120px">
                <v-overlay-loader :loading="historyLoading" text="Geçmiş yükleniyor"/>
                <div v-if="!historyLoading && !history.length" class="text-center text-muted p-2">
                    Bu sözleşme için henüz belge yüklenmemiş.
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-sm mb-0">
                        <thead>
                            <tr>
                                <th class="text-nowrap">Sürüm</th>
                                <th class="text-nowrap">Dosya Adı</th>
                                <th class="text-nowrap">Boyut</th>
                                <th class="text-nowrap">Yükleyen</th>
                                <th class="text-nowrap">Tarih</th>
                                <th>Değişiklik Notu</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in history" :key="item.FileId">
                                <td class="text-nowrap">
                                    <b-badge :variant="item.IsCurrent ? 'success' : 'secondary'">v{{ item.Version }}</b-badge>
                                    <span v-if="item.IsCurrent" class="ml-50 text-success font-weight-bolder">Güncel</span>
                                </td>
                                <td class="text-nowrap">{{ item.FileName }}{{ item.FileExtension }}</td>
                                <td class="text-nowrap">{{ item.FileSizeStr }}</td>
                                <td class="text-nowrap">{{ item.UploadedByUserName || '-' }}</td>
                                <td class="text-nowrap">{{ item.UploadedAt }}</td>
                                <td><span class="text-muted">{{ item.RevisionNote || '-' }}</span></td>
                                <td class="text-right">
                                    <button
                                        class="btn btn-sm btn-primary btn-icon text-nowrap"
                                        :disabled="downloadingFileId == item.FileId"
                                        v-on:click="downloadVersion(item)">
                                        <b-spinner small v-if="downloadingFileId == item.FileId"></b-spinner>
                                        <feather-icon v-else icon="DownloadIcon" size="14"></feather-icon>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </b-modal>

        <div class="row justify-content-between align-items-center">
            <div class="col-md mb-1 mb-md-0">
                <label class="form-label d-block mb-25">Sözleşme Belgesi</label>
                <div v-if="hasDocument" class="d-flex align-items-center flex-wrap">
                    <b-badge variant="success" class="mr-50">v{{ agreement.AgreementDocumentVersion }}</b-badge>
                    <span class="font-weight-bolder mr-50">{{ agreement.AgreementDocumentName }}.pdf</span>
                    <small class="text-muted">
                        {{ agreement.AgreementDocumentUploadedAt }}
                        <template v-if="agreement.AgreementDocumentUploadedByUserName">
                            · {{ agreement.AgreementDocumentUploadedByUserName }}
                        </template>
                    </small>
                </div>
                <div v-else class="text-muted">Henüz belge yüklenmemiş.</div>
            </div>
            <div class="col-md-auto">
                <div class="row no-gutters">
                    <div class="col-auto pr-50" v-if="hasDocument">
                        <button class="btn btn-primary text-nowrap waves-effect" :disabled="currentDownloading" v-on:click="downloadCurrent">
                            <b-spinner small v-if="currentDownloading" class="mr-50"></b-spinner>
                            <feather-icon v-else icon="DownloadIcon" class="mr-50" size="14"></feather-icon>
                            İndir
                        </button>
                    </div>
                    <div class="col-auto pr-50">
                        <button class="btn btn-success text-nowrap waves-effect" v-on:click="showUpload">
                            <feather-icon icon="UploadIcon" class="mr-50" size="14"></feather-icon>
                            {{ hasDocument ? 'Belgeyi Güncelle' : 'Belge Yükle' }}
                        </button>
                    </div>
                    <div class="col-auto" v-if="versionCount > 0">
                        <button class="btn btn-outline-secondary text-nowrap waves-effect" v-on:click="showHistory">
                            <feather-icon icon="ClockIcon" class="mr-50" size="14"></feather-icon>
                            Geçmiş ({{ versionCount }})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BAlert, BBadge, BFormTextarea, BSpinner } from 'bootstrap-vue'
import { serialize } from 'object-to-formdata'
import { AgreementApi } from '@/api/agreement'
import Additional from '@/utilities/additional'
import VExecuter from '@/components/VExecuter.vue'
import VOverlayLoader from '@/components/VOverlayLoader.vue'
import VFileUploader from '@/components/v-file-uploader/VFileUploader.vue'

export default {
    components:{
        BAlert,
        BBadge,
        BFormTextarea,
        BSpinner,
        VExecuter,
        VOverlayLoader,
        VFileUploader
    },
    props:{
        /**
         * Sözleşme detay yanıtı. Belge alanları (AgreementDocumentUrl, ...Version,
         * ...UploadedAt, ...VersionCount) buradan okunur.
         */
        agreement:{
            type:Object,
            required:true
        }
    },
    data(){
        return{
            File:{
                AgreementDocument:null
            },
            RevisionNote:null,
            fileUploading:false,
            history:[],
            historyLoading:false,
            downloadingFileId:null,
            currentDownloading:false
        }
    },
    computed:{
        hasDocument(){
            return !!this.agreement.AgreementDocumentUrl
        },
        versionCount(){
            return this.agreement.AgreementDocumentVersionCount || 0
        },
        canUpload(){
            return !!this.File.AgreementDocument && !!(this.RevisionNote || '').trim()
        }
    },
    methods:{
        showUpload(){
            this.resetForm()
            this.$showModal('update-agreement-document')
        },
        showHistory(){
            this.$showModal('agreement-document-history')
            this.getHistory()
        },
        resetForm(){
            this.File.AgreementDocument = null
            this.RevisionNote = null
        },
        getHistory(){
            this.historyLoading = true
            AgreementApi.DocumentHistory(this.agreement.Id).then(res=>{
                this.history = res.data.Data || []
                this.historyLoading = false
            }).catch(err=>{
                this.$result(null,err)
                this.historyLoading = false
            })
        },
        fileUpload(){
            this.fileUploading = true
            const data = {
                AgreementDocument: this.File.AgreementDocument,
                AgreementId: this.agreement.Id,
                RevisionNote: (this.RevisionNote || '').trim()
            }
            const x = serialize(data,{indices: true,dotsForObjectNotation: true,nullsAsUndefineds:true})
            AgreementApi.UploadAgreementDocument(x).then(res=>{
                this.$result(res)
                this.fileUploading = false
                if(res.data.Success){
                    this.$hideModal('update-agreement-document')
                    this.resetForm()
                    this.history = []
                    // Üst ekran detayı yeniden çeker; güncel sürüm bilgisi oradan gelir.
                    this.$emit('updated')
                }
            }).catch(err=>{
                this.$result(null,err)
                this.fileUploading = false
            })
        },
        downloadCurrent(){
            this.currentDownloading = true
            this.download(this.agreement.AgreementDocumentUrl, this.documentFileName(this.agreement.AgreementDocumentName, this.agreement.AgreementDocumentVersion))
                .finally(()=>{ this.currentDownloading = false })
        },
        downloadVersion(item){
            this.downloadingFileId = item.FileId
            this.download(item.FileUrl, this.documentFileName(item.FileName, item.Version))
                .finally(()=>{ this.downloadingFileId = null })
        },
        download(url,name){
            return Additional.GetBlobFile(url).then(res=>{
                Additional.DownloadFile(res.data,name)
            }).catch(err=>{
                this.$result(null,err)
            })
        },
        /**
         * Aynı sözleşmenin farklı sürümleri aynı adla inmesin diye dosya adına sürüm eklenir.
         */
        documentFileName(name,version){
            const base = name || 'Sözleşme'
            return version ? `${base} - v${version}.pdf` : `${base}.pdf`
        }
    }
}
</script>
